import { createElement } from "react";
import { DateTime } from "luxon";
import { AgGrid, Button } from "@jsq/ds/core";
import { actionButton } from "../../../../common/components/ActionButton/styles_vanilla.css";
import { ClientFilter } from "../../../GridFilters/ClientFilter";
import { PayeeNameFilter } from "../../../GridFilters/PayeeNameFilter";
import { PayeeTypeFilter } from "../../../GridFilters/PayeeTypeFilter";
import { PayeeDisplayStatusTypes } from "../../constants";
import { verticalCenterWrapper } from "./styles_vanilla.css";
import { PayeeRowItem } from "./types";

/**
 * List of the supported columns in the Payee table
 * (ordered from left to right)
 */
export enum SupportedColumns {
  Payee = "payeeName",
  Client = "clientName",
  Type = "payeeType",
  ReceivingAccount = "receivingAccount",
  ModifiedDate = "modifiedAt",
  ApprovalStatus = "status",
  Action = "action",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.Client, "Client"],
  [SupportedColumns.Type, "Type"],
  [SupportedColumns.Payee, "Payee"],
  [SupportedColumns.ReceivingAccount, "Receiving account"],
  [SupportedColumns.ModifiedDate, "Modified date"],
  [SupportedColumns.Action, "Action"],
]);

/**
 * AG Grid Column definitions for the Payee Table
 */
export const columnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.Client,
    headerName: columnNames.get(SupportedColumns.Client),
    filter: ClientFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.Type,
    headerName: columnNames.get(SupportedColumns.Type),
    filter: PayeeTypeFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    cellStyle: {
      textTransform: "capitalize",
    },
  },
  {
    field: SupportedColumns.Payee,
    headerName: columnNames.get(SupportedColumns.Payee),
    filter: PayeeNameFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    minWidth: 360,
  },
  {
    field: SupportedColumns.ReceivingAccount,
    headerName: columnNames.get(SupportedColumns.ReceivingAccount),
  },
  {
    field: SupportedColumns.ModifiedDate,
    headerName: columnNames.get(SupportedColumns.ModifiedDate),
    valueFormatter: ({ data }: { data: PayeeRowItem }) => {
      return data.modifiedAt.toLocaleString(DateTime.DATE_MED);
    },
  },
  {
    field: SupportedColumns.ApprovalStatus,
    headerName: columnNames.get(SupportedColumns.ApprovalStatus),
    resizable: false,
  },
  {
    field: SupportedColumns.Action,
    headerName: columnNames.get(SupportedColumns.Action),
    sortable: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    flex: 0,
    width: 108,
    pinned: "right",
    cellClass: verticalCenterWrapper,
    cellRenderer: ({ buttonText }: { buttonText: string }) => {
      return buttonText
        ? createElement(Button, { className: actionButton, variant: "neutral" }, buttonText)
        : null;
    },
    cellRendererParams: ({ data }: { data: PayeeRowItem }) => ({
      buttonText:
        data.status === String(PayeeDisplayStatusTypes.Pending) ? "Review" : undefined,
    }),
  },
];
